import { useMemo } from "react";

/**
 * CLIENT FILM-STRIP — minimal editorial variant
 * ──────────────────────────────────────────────
 * Auto-scrolling marquee of client portraits. No chunky cinema-reel
 * chrome: just hair-thin rules with tick marks (a quiet nod to film
 * sprockets) and the photos themselves, on the page's natural cream.
 * B&W by default, lifts into colour + a touch upward on hover. Photos
 * cannot be right-clicked, dragged, or saved.
 *
 * The track is doubled in the DOM and animated with a single CSS
 * keyframe (translateX -50%) for a seamless loop.
 */
const CLIENT_PHOTOS = Array.from({ length: 22 }, (_, i) => `/clients/${i + 1}.jpg`);

export default function ClientFilmStrip({ heading = "and everywhere in between", testid = "client-filmstrip" }) {
  const reel = useMemo(() => [...CLIENT_PHOTOS, ...CLIENT_PHOTOS], []);
  const noSave = (e) => e.preventDefault();

  return (
    <div className="mt-12 md:mt-16" data-testid={testid}>
      {/* Quiet headline — small kicker, italic serif, centered */}
      <div className="text-center mb-6 md:mb-8">
        <p className="overline text-ink/40 mb-2">cont&apos;d</p>
        <h3 className="font-serif italic text-2xl md:text-3xl text-ink/85 leading-none">
          {heading}
        </h3>
      </div>

      {/* The strip — pure cream, only hairlines + ticks */}
      <div
        className="film-strip-min relative overflow-hidden select-none"
        onContextMenu={noSave}
        onDragStart={noSave}
        aria-label="Selected clients"
      >
        <TickRule />

        <div className="film-min-track">
          {reel.map((src, i) => (
            <figure
              key={i}
              className="film-min-frame"
              onContextMenu={noSave}
              onDragStart={noSave}
            >
              <img
                src={src}
                alt=""
                draggable={false}
                onContextMenu={noSave}
                onDragStart={noSave}
                loading={i < 12 ? "eager" : "lazy"}
                fetchPriority={i < 6 ? "high" : "auto"}
                decoding="async"
                className="film-min-img"
                aria-hidden="true"
              />
              <span className="film-min-cover" aria-hidden="true" />
            </figure>
          ))}
        </div>

        <TickRule />

        {/* Soft edge fades to cream — hides the loop seam */}
        <div className="film-min-fade film-min-fade-l" aria-hidden="true" />
        <div className="film-min-fade film-min-fade-r" aria-hidden="true" />
      </div>
    </div>
  );
}

function TickRule() {
  // 24 evenly-spaced hair-thin ticks on a 1px baseline
  const ticks = Array.from({ length: 24 });
  return (
    <div className="film-min-rule" aria-hidden="true">
      {ticks.map((_, i) => (
        <span key={i} className="film-min-tick" />
      ))}
    </div>
  );
}
